import { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useOnboardingStore } from "../../store/onboardingSlice";
import { createCategory, saveOnboardingStep } from "../../api/auth";
import { trackOnboardingStepComplete } from "../../lib/analytics";

interface CategoryEntry {
  service_category_id: string;
  name: string;
}

export default function CategoriesStep() {
  const { setCurrentStep, setStepData, setSaving, saving } = useOnboardingStore();
  const [categories, setCategories] = useState<CategoryEntry[]>([]);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!name.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const created = await createCategory({ name: name.trim(), sort_order: categories.length });
      setCategories((c) => [...c, { service_category_id: created.service_category_id, name: created.name }]);
      setName("");
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      const serverMsg = axiosErr?.response?.data?.error;
      console.error("createCategory error:", axiosErr?.response?.data ?? axiosErr?.message);
      setError(serverMsg || axiosErr?.message || "Failed to add category. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") { e.preventDefault(); handleAdd(); }
  };

  const handleContinue = async () => {
    setError(null);
    setSaving(true);
    try {
      setStepData("categories", categories);
      await saveOnboardingStep({ step: "categories", step_status: categories.length > 0 ? "complete" : "skipped" });
      trackOnboardingStepComplete(2, "service_categories", true);
      setCurrentStep(3);
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await saveOnboardingStep({ step: "categories", step_status: "skipped" });
      trackOnboardingStepComplete(2, "service_categories", true);
      setCurrentStep(3);
    } catch {
      setError("Failed to skip categories. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>Service categories</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Group your services into categories (e.g. Massage, Facials). You can skip this and add categories later.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField
          label="Category name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
          size="small"
          autoFocus
        />
        <Button
          variant="outlined"
          onClick={handleAdd}
          disabled={!name.trim() || adding}
          sx={{ whiteSpace: "nowrap" }}
        >
          {adding ? <CircularProgress size={18} /> : "Add"}
        </Button>
      </Box>

      {categories.length > 0 && (
        <List dense sx={{ mb: 3, border: 1, borderColor: "divider", borderRadius: 1 }}>
          {categories.map((cat, i) => (
            <ListItem key={cat.service_category_id} divider={i < categories.length - 1}>
              <ListItemText primary={cat.name} />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => setCategories((c) => c.filter((_, idx) => idx !== i))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={saving}
          sx={{ flex: 1 }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : "Continue"}
        </Button>
        <Button variant="text" onClick={handleSkip} disabled={saving}>
          Skip for now
        </Button>
      </Box>
    </Box>
  );
}
